import {
  Button,
  Dialog,
  Group,
  MantineProvider,
  Text
} from "@mantine/core";
import Cookies from 'js-cookie';
import React, { createContext, useContext, useEffect, useMemo } from "react";
import CookieConsent from "react-cookie-consent";
import ReactGA from 'react-ga';
import { useLocation, useNavigate } from "react-router-dom";
import { Socket, connect } from "socket.io-client";
import usePersistState from "../hooks/usePersistState";
import { GET_COMPANY_INTEGRATION } from "../services/companies";
import { useQuery } from "../utility/util";
import { useAuth } from "./auth.context";
import { useLanguage } from "./language.context";

interface ConfirmDialogProps {
  (
    props: { text: string; buttonText?: string },
    callback: (props: { confirmed: boolean }) => void
  ): void;
}
interface ConfigsProps {
  primary?: string; navbar?: string; image?: string; icon?: string;
  contrast?: string; brand?: string;
  name?: string;
}
interface AppContextProps {
  confirmDialog: ConfirmDialogProps;
  setColorScheme: (colorScheme: "dark" | "light") => void;
  socket: Socket | null;
  configs: ConfigsProps;
  setConfigs: (vl: ConfigsProps) => any;
  mobileApp?: any;
  registration?: ServiceWorkerRegistration;
  openedSidebar?: boolean;
  toggleOpenedSidebar?: () => void;
  notifyGaEvent: (params: any) => void;
}

const AppContext = createContext<AppContextProps>({} as AppContextProps);

export const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const lsColorScheme = localStorage.getItem("@kapo/colorScheme") as
    | "light"
    | "dark";

  const [openedConfirm, setOpenedConfirm] = React.useState<any>(null);
  const [configs, setConfigs] = React.useState<ConfigsProps>({});
  const [mobileApp, setMobileApp] = React.useState<any>({});
  const [colorScheme, setColorScheme] = React.useState<"dark" | "light">(
    lsColorScheme || "light"
  );
  const [registration, setRegistration] =
    React.useState<ServiceWorkerRegistration | null>(null);

  const query = useQuery();

  let fbc = query.get("fbclid");

  const [socket, setSocket] = React.useState<Socket | null>(null);

  const { role, user: currentUser, companyHost, loadUserData, user, userData } = useAuth();
  const { str } = useLanguage();

  const { pathname } = useLocation();
  const navigate = useNavigate();


  const gaTrackings = [
    "G-LY81V86E4J",
    companyHost?.ga,
  ].filter(nn => nn);

  const [openedSidebar, setOpenedSidebar] = usePersistState<boolean>(
    "@kapo/opened-sidebar",
    false
  );

  const confirmDialog: ConfirmDialogProps = (
    { text, buttonText = str("CONFIRM") },
    callback
  ) => {
    setOpenedConfirm({ text, buttonText, callback });
  };

  const toggleOpenedSidebar = () => {
    setOpenedSidebar(!openedSidebar);
  };

  const notifyGaEvent = (params: any) => {
    for (let trackingId of gaTrackings) {
      ReactGA.event(params, trackingId);
    }
  }

  // useEffect(() => {
  //   GET_COMPANY_INTEGRATION("mobile-app")
  //     .then((res) => { setMobileApp(res) })
  //     .catch(err => { })
  // }, [userData])

  useEffect(() => {
    for (let trackingId of gaTrackings) {
      ReactGA.initialize(trackingId);
      ReactGA.set({ debug: false });
    }
  }, [JSON.stringify(gaTrackings)]);

  useEffect(() => {
    for (let trackingId of gaTrackings) {
      ReactGA.pageview(pathname, [trackingId]);
    }
  }, [JSON.stringify(gaTrackings), pathname]);

  // useEffect(() => {
  //   if ("serviceWorker" in navigator) {
  //     navigator.serviceWorker
  //       .register("/service-worker.js")
  //       .then((registration) => {
  //         setRegistration(registration);
  //       })
  //       .catch((error) => {
  //         console.error("Service worker registration failed:", error);
  //       });
  //   }
  // }, []);


  useEffect(() => {
    if (fbc) Cookies.set("fbc", fbc);
  }, [fbc])

  useEffect(() => {
  }, [socket]);

  useEffect(() => {
    localStorage.setItem("@kapo/colorScheme", colorScheme);
  }, [colorScheme]);

  useEffect(() => {
    if (currentUser) {
      const s = connect(process.env.REACT_APP_SERVER_URL, {
        extraHeaders: {
          Authorization: `Bearer ${currentUser.token}`,
        },
      });
      setSocket(s);
    }
  }, [currentUser]);

  useEffect(() => {
    setConfigs(c => ({
      ...c,
      name: userData?.company?.name,
      primary: companyHost?.appearance?.colors?.primary || userData?.company?.appearance?.colors?.primary || "#FF4D17",
      navbar: companyHost?.appearance?.colors?.navbar || userData?.company?.appearance?.colors?.navbar || "white",
      image: companyHost?.image ?? userData?.company?.image,
      icon: companyHost?.icon ?? userData?.company?.icon,
    }))
  }, [companyHost, userData])

  const value = useMemo<AppContextProps>(() => {
    return {
      confirmDialog,
      setColorScheme,
      socket,
      mobileApp,
      registration,
      openedSidebar,
      toggleOpenedSidebar,
      notifyGaEvent,
      configs: {
        ...configs,
        brand: {
          "black": "#000000",
          "green": "#00DB72",
          "yellow": "#FFD540",
          "violet": "#FF4D17",
          "white": "#FBFBFB",
          "blue": "#0A39FC",
          "pink": "#FC0A6B",
        }[configs.navbar ?? "white"],
        contrast: {
          "black": "#FBFBFB",
          "green": "#000000",
          "yellow": "#000000",
          "violet": "#FBFBFB",
          "blue": "#FBFBFB",
          "pink": "#FBFBFB",
          "white": "#000000",
        }[configs.navbar ?? "white"],
      },
      setConfigs: (c) => {
        setConfigs(v => ({ ...v, ...c }));
      },
    };
  }, [
    configs,
    setConfigs,
    confirmDialog,
    setColorScheme,
    socket,
    registration,
    openedSidebar,
    toggleOpenedSidebar,
  ]);

  return (
    <MantineProvider
      withCssVariables
      theme={{
        fontFamily: "Inter, sans-serif",
        headings: { fontWeight: "bold" },
        primaryColor: "dark",
        colors: {
          // gray: ["#8a8a8a", "#8a8a8a", "#8a8a8a", "#8a8a8a", "#CCCCEC", "#8a8a8a", "#8a8a8a", "#8a8a8a", "#CCCCEC", "#8a8a8a"],
          // dark: [
          //   '#FAFAFA',
          //   '#acaebf',
          //   '#8c8fa3',
          //   '#666980',
          //   '#0b7285',
          //   '#0b7285',
          //   '#04648b',
          //   '#0b7285',
          //   '#0c0d21',
          //   '#01010a',
          // ],
        },
        components: {
          Table: {
            styles: {
              root: {
                border: '1px solid #f2f2f2',
                borderRadius: 15,
              },
              th: {
                backgroundColor: "#f2f2f2",
                padding: '20px 10px',
                color: '#03060D',
                fontWeight: "bold",
                textAlign: 'center',
              },
              td: {
                backgroundColor: "#f2f2f2",
                padding: '10px 10px',
              },
            }
          },
          TextInput: { styles: { input: { color: '#393939', backgroundColor: '#FAFAFA' } } },
          Textarea: { styles: { input: { color: '#393939', backgroundColor: '#FAFAFA' } } },
          Select: { styles: { input: { color: '#393939', backgroundColor: '#FAFAFA' } } },
          MultiSelect: { styles: { input: { color: '#393939', backgroundColor: '#FAFAFA' } } },
          DateTimePicker: { styles: { input: { color: '#393939', backgroundColor: '#FAFAFA' } } },
          DatePickerInput: { styles: { input: { color: '#393939', backgroundColor: '#FAFAFA' } } },
          TimeInput: { styles: { input: { color: '#393939', backgroundColor: '#FAFAFA' } } },
          PasswordInput: { styles: { input: { color: '#393939', backgroundColor: '#FAFAFA' } } },
          // Box: { styles: { input: { color: '#393939', backgroundColor: '#FAFAFA' } } },
          Paper: { styles: { root: { borderRadius: '4px' } } },
        },
      }}
      // defaultColorScheme="dark"
    defaultColorScheme="light"
    // forceColorScheme="dark"
    >
      <AppContext.Provider value={value}>
        <>
          <>
            <Dialog
              opened={Boolean(openedConfirm)}
              withCloseButton={false}
              onClose={() => setOpenedConfirm(null)}
              size="lg"
              radius="md"
              zIndex={9999}
            >
              <Text size="sm" mb="sm">
                {openedConfirm?.text}
              </Text>

              <Group align="flex-end">
                <Button variant="outline" color="gray"
                  onClick={() => setOpenedConfirm(null)}
                >{str("CANCEL")}</Button>
                <Button
                  color="dark"
                  onClick={() => {
                    openedConfirm?.callback &&
                      openedConfirm?.callback({ confirmed: true });
                    setOpenedConfirm(null);
                  }}
                >
                  {openedConfirm?.buttonText}
                </Button>
              </Group>
            </Dialog>
          </>
        </>
        {children}
        {/* {(companyHost && !companyHost.active) ? <PublicPage /> : children} */}
        {/* <Helmet>
          {configs.name && <title>{configs.name}</title>}
          {configs.icon
            ? <link rel="icon" href={configs.icon} />
            : <link rel="icon" href={"/icon.png"} />}
        </Helmet> */}
        {/* {userData?.user && role?.profile?.title !== "Client" && process.env.REACT_APP_SUPORT_BOARD_ID && <Helmet>
          <script id="chat-init" src={`https://cloud.board.support/account/js/init.js?id=${process.env.REACT_APP_SUPORT_BOARD_ID}&name=${userData.user.name}`}></script>
        </Helmet>} */}

        <CookieConsent
          style={{
            background: configs.primary ?? '#014040', fontWeight: 'bold'
          }}
          buttonStyle={{
            color: "white",
            background: 'transparent',
            fontWeight: "bold",
            border: '2px solid white',
            borderRadius: 8,
            padding: '5px 30px'
          }}
        >
          {str("COOKIES_DESCRIPTION")}{" "}
        </CookieConsent>
      </AppContext.Provider>
    </MantineProvider>
  );
};

export const useApp = () => useContext(AppContext);
